import React, { useState } from "react";

// Group results by segment, family, and class
function groupResultsByCategory(results) {
  const groups = [];
  const groupMap = new Map();

  results.forEach(result => {
    // Content is now a JSON object, not a string
    const fields = result.payload?.content || {};
    const groupKey = `${fields.SEGMENT_CODE}-${fields.FAMILY_CODE}-${fields.CLASS_CODE}`;
    
    if (!groupMap.has(groupKey)) {
      const group = {
        segment: { title: fields.SEGMENT_TITLE, code: fields.SEGMENT_CODE },
        family: { title: fields.FAMILY_TITLE, code: fields.FAMILY_CODE },
        class: { title: fields.CLASS_TITLE, code: fields.CLASS_CODE },
        items: []
      };
      groupMap.set(groupKey, group);
      groups.push(group);
    }
    
    groupMap.get(groupKey).items.push({
      name: fields.ITEM_NAME,
      code: fields.UNSPSC_CODE
    });
  });

  return groups;
}

export default function UNSPSCResultsTable({ results }) {
  const [currentIndex, setCurrentIndex] = useState(0);

  if (!results || results.length === 0) {
    return <div>No UNSPSC results found.</div>;
  }

  const groups = groupResultsByCategory(results);

  const nextGroup = () => {
    setCurrentIndex((prev) => (prev + 1) % groups.length);
  };

  const prevGroup = () => {
    setCurrentIndex((prev) => (prev - 1 + groups.length) % groups.length);
  };

  const goToGroup = (index) => {
    setCurrentIndex(index);
  };

  const currentGroup = groups[currentIndex];

  return (
    <div className="unspsc-container">
      {/* Header with navigation */}
      <div className="unspsc-header">
        <button
          onClick={prevGroup}
          disabled={groups.length <= 1}
          className="unspsc-nav-button"
        >
          Previous
        </button>
        
        <div className="unspsc-counter">
          <span>Group {currentIndex + 1} of {groups.length}</span>
          
          {/* Dots indicator */}
          <div className="unspsc-dots">
            {groups.map((_, index) => (
              <button
                key={index}
                onClick={() => goToGroup(index)}
                className={`unspsc-dot ${index === currentIndex ? 'active' : ''}`}
                aria-label={`Go to group ${index + 1}`}
              />
            ))}
          </div>
        </div>
        
        <button
          onClick={nextGroup}
          disabled={groups.length <= 1}
          className="unspsc-nav-button"
        >
          Next
        </button>
      </div>

      {/* Table container with smooth transition */}
      <div className="unspsc-table-container">
        <table className="unspsc-table">
          <thead>
            <tr>
              <th>Attribute</th>
              <th>Value</th>
              <th>Code</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Segment Title</td>
              <td>{currentGroup.segment.title || ""}</td>
              <td>{currentGroup.segment.code || ""}</td>
            </tr>
            <tr>
              <td>Family Title</td>
              <td>{currentGroup.family.title || ""}</td>
              <td>{currentGroup.family.code || ""}</td>
            </tr>
            <tr>
              <td>Class Title</td>
              <td>{currentGroup.class.title || ""}</td>
              <td>{currentGroup.class.code || ""}</td>
            </tr>
            {/* Display all items in this group */}
            {currentGroup.items.map((item, idx) => (
              <tr key={idx}>
                <td><b>{idx === 0 ? 'Item Name' : ''}</b></td>
                <td><b>{item.name || ""}</b></td>
                <td><b>{item.code || ""}</b></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      {/* Footer with quick navigation */}
      <div className={`unspsc-footer ${groups.length <= 1 ? 'hidden' : ''}`}>
        {currentGroup.items.length} item(s) in this category. {groups.length > 1 ? 'Use buttons or dots to navigate groups.' : ''}
      </div>
    </div>
  );
}